import type { LabeledDateTone } from '@artic/ui/labeled-date';

import type { ProjectPlanningListItem } from './types';

/**
 * The lifecycle milestone a project list row should surface in place of a
 * bare "Created" date. Precedence is most-specific-wins:
 *
 *   Archived  — `archived_at` is set (terminal; overrides everything else)
 *   Completed — every non-archived work order is complete (`project_derived_status`)
 *   Released  — the project has been released into production
 *   Created   — still a draft
 *
 * The chosen milestone's own date may be null (e.g. a legacy released project
 * with no `released_at`); `<LabeledDate>` renders a placeholder for that.
 */
export interface ProjectMilestone {
  label: 'Created' | 'Released' | 'Completed' | 'Archived';
  date: string | null;
  tone: LabeledDateTone;
}

export function resolveProjectMilestone(
  project: Pick<
    ProjectPlanningListItem,
    | 'project_status'
    | 'project_derived_status'
    | 'created_at'
    | 'released_at'
    | 'completed_at'
    | 'archived_at'
  >,
): ProjectMilestone {
  if (project.archived_at) {
    return { label: 'Archived', date: project.archived_at, tone: 'muted' };
  }
  if (project.project_derived_status === 'complete') {
    return { label: 'Completed', date: project.completed_at, tone: 'success' };
  }
  if (project.project_status === 'released') {
    return { label: 'Released', date: project.released_at, tone: 'info' };
  }
  return { label: 'Created', date: project.created_at, tone: 'neutral' };
}
